import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SectionEditProps } from '@/components/onboarding/OnboardingTimeline';
import { CatalogStep } from '@/components/onboarding/kb-form/CatalogStep';
import { EssenceStep } from '@/components/onboarding/kb-form/EssenceStep';
import { KbSizeIndicator } from '@/components/onboarding/kb-form/KbSizeIndicator';
import { ObjectiveStep } from '@/components/onboarding/kb-form/ObjectiveStep';
import { OfferModalityStep } from '@/components/onboarding/kb-form/OfferModalityStep';
import { OperationStep } from '@/components/onboarding/kb-form/OperationStep';
import { ReviewStep } from '@/components/onboarding/kb-form/ReviewStep';
import { StepProgress } from '@/components/onboarding/kb-form/StepProgress';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import {
  DEFAULT_KB_PROFILE_VALUES,
  estimateKbChars,
  fromKbProfile,
  reconcileOperation,
  toSubmitPayload,
  useKbProfileForm,
  type KbProfileFormValues,
} from '@/hooks/useKbProfileForm';
import { apiErrorMessage } from '@/lib/apiErrorMessage';
import { focusFirstError, prefersReducedMotion } from '@/lib/formFocus';
import { onboardingService } from '@/services/onboardingService';
import type { Modality } from '@/services/kbProfileService';

const TOTAL_STEPS = 6;
const DEBOUNCE_MS = 1000;

/** localStorage key for the resume cursor (F094). Scoped per user id so a
 * shared browser doesn't leak one account's position into another's. */
function cursorKey(userId: number | undefined): string | null {
  return userId != null ? `kb_wizard_step:${userId}` : null;
}

function readCursor(key: string | null): number | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isInteger(n) && n >= 0 && n < TOTAL_STEPS ? n : null;
  } catch {
    return null;
  }
}

/** Step indices */
const STEP_OFFERS_MODALITIES = 0;
const STEP_ESSENCE = 1;
const STEP_CATALOG = 2;
const STEP_OPERATION = 3;
const STEP_OBJECTIVE = 4;
const STEP_REVIEW = 5;

/** Fields to validate before advancing from each step. */
const STEP_FIELDS: Array<Array<string>> = [
  ['offers', 'modalities'],
  ['one_liner', 'ideal_customer'],
  ['catalog'],
  ['operation'],
  ['bot_goal', 'handoff_contact'],
  [],
];

/** Flat field order across steps 1→5, for focusing the first error on
 * activate (which validates the whole form at once). */
const ALL_FIELDS_IN_VISUAL_ORDER: string[] = STEP_FIELDS.flat();

const STEP_TITLE_KEYS = [
  'onboarding.kbForm.step1.title',
  'onboarding.kbForm.step2.title',
  'onboarding.kbForm.step3.title',
  'onboarding.kbForm.step4.title',
  'onboarding.kbForm.step5.title',
  'onboarding.kbForm.step6.title',
];

export function KbSection({ editing = false, onEditDone }: SectionEditProps = {}) {
  const { t, i18n } = useTranslation();
  const { state, kbProfile, profile, submitKbProfile } = useOnboardingState();
  const { user } = useAuth();

  // F094 — resume cursor. Restore from localStorage only during first
  // configuration (KB incomplete ⟺ not editing); an edit session always
  // reopens at step 1 and navigates by clicking StepProgress.
  const storageKey = cursorKey(user?.id);
  const [currentStep, setCurrentStep] = useState(() =>
    editing ? 0 : (readCursor(storageKey) ?? 0)
  );
  // Highest step reachable via StepProgress: every step while editing a
  // completed KB; only visited steps during first-config (no skipping ahead
  // past unvalidated steps).
  const [maxReached, setMaxReached] = useState(() => (editing ? TOTAL_STEPS - 1 : currentStep));
  const [submitError, setSubmitError] = useState<string | null>(null);
  // F069 #10 — handleActivate is a plain onClick (not form.handleSubmit), so
  // RHF's formState.isSubmitting never flips; track submission locally.
  const [submitting, setSubmitting] = useState(false);

  // Determine initial values: draft from state > committed profile > defaults.
  // Computed once on first render via useRef so RHF is not re-created on context
  // refreshes — the form is the source of truth for the session once mounted.
  const initialValuesRef = useRef<KbProfileFormValues | null>(null);
  if (!initialValuesRef.current) {
    if (state?.partial_kb_form && Object.keys(state.partial_kb_form).length > 0) {
      initialValuesRef.current = {
        ...DEFAULT_KB_PROFILE_VALUES,
        ...(state.partial_kb_form as Partial<KbProfileFormValues>),
      };
    } else {
      const fromProfile = fromKbProfile(kbProfile);
      if (Object.keys(fromProfile).length > 0) {
        initialValuesRef.current = { ...DEFAULT_KB_PROFILE_VALUES, ...fromProfile };
      } else {
        initialValuesRef.current = DEFAULT_KB_PROFILE_VALUES;
      }
    }
  }
  const initialValues = initialValuesRef.current;

  const form = useKbProfileForm({
    modalities: initialValues.modalities ?? [],
    initial: initialValues,
  });

  // The required-field set (offers/modalities/bot_goal/handoff) is modality-
  // independent and operation fields are all-optional, so a fixed resolver is
  // correct. The modality axis instead drives (a) which operation inputs render
  // (OperationStep) and (b) pruning orphan operation values on change — the
  // authoritative size/shape guard is toSubmitPayload's reconcileOperation at
  // submit; this effect keeps the live draft clean as modalities change.
  const watchedModalities: Modality[] = form.watch('modalities') ?? [];
  const watchedOffers = form.watch('offers') ?? [];

  useEffect(() => {
    const currentOp = form.getValues('operation') ?? {};
    form.setValue('operation', reconcileOperation(currentOp, watchedModalities, watchedOffers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedModalities), JSON.stringify(watchedOffers)]);

  // Debounced draft save. F063: gated off in edit mode — drafts exist to
  // resume the first-configuration flow; an edit session must leave zero
  // side effects until the explicit save (Cancelar discards everything).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (editing) return;
    const subscription = form.watch((values) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onboardingService
          .updateState({ partial_kb_form: values as Record<string, unknown> })
          .catch(() => {
            // best-effort draft save — silently ignore
          });
      }, DEBOUNCE_MS);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [form, editing]);

  // Persist the cursor + track the high-water mark whenever the step changes.
  useEffect(() => {
    setMaxReached((m) => Math.max(m, currentStep));
    if (editing || !storageKey) return;
    try {
      localStorage.setItem(storageKey, String(currentStep));
    } catch {
      // best-effort — a full/locked storage must not break navigation
    }
  }, [currentStep, editing, storageKey]);

  // Scroll the form back to its top each time the internal step changes. The
  // KB form is a multi-step wizard nested inside a single timeline node, so the
  // OnboardingTimeline's cross-section scroll never fires here; without this,
  // advancing from a tall section (lists the owner grew by adding items) leaves
  // the next section's heading scrolled off-screen below the fold.
  const sectionTopRef = useRef<HTMLDivElement>(null);
  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    sectionTopRef.current?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [currentStep]);

  function clearCursor() {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // best-effort
    }
  }

  async function handleNext() {
    const fields = STEP_FIELDS[currentStep];
    if (fields.length > 0) {
      const valid = await form.trigger(fields as never);
      if (!valid) {
        focusFirstError(form.formState.errors, fields);
        return;
      }
    }

    // When leaving step 1, pre-select the default family if still empty
    // (F077: the modality heuristic was retired with the 8-way taxonomy —
    // capture_contacts is the spec default).
    if (currentStep === STEP_OFFERS_MODALITIES && !form.getValues('bot_goal')) {
      form.setValue('bot_goal', 'capture_contacts');
    }

    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  }

  function handleBack() {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  async function handleActivate() {
    // Validate all fields before submitting
    const valid = await form.trigger();
    if (!valid) {
      focusFirstError(form.formState.errors, ALL_FIELDS_IN_VISUAL_ORDER);
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = toSubmitPayload(form.getValues());
      await submitKbProfile(payload);
      // F094 — KB activated, the resume cursor is no longer meaningful.
      clearCursor();
      // F063 — in edit mode the save collapses the node; the funnel pointer
      // is preserved server-side (mark re-resolves to the first incomplete).
      if (editing) onEditDone?.();
    } catch (err) {
      // F093/A04 — never render the raw backend/network message at the owner.
      setSubmitError(apiErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  const formValues = form.watch();

  return (
    <div data-testid="section-kb" className="space-y-4 scroll-mt-20" ref={sectionTopRef}>
      {/* Anchor for scroll-to-top on step change. `scroll-mt` keeps the heading
          clear of the sticky app header when scrolled into view. */}
      <StepProgress
        current={currentStep}
        total={TOTAL_STEPS}
        onStepClick={setCurrentStep}
        maxReached={maxReached}
      />

      <p className="text-sm font-medium">{t(STEP_TITLE_KEYS[currentStep])}</p>

      <Form {...form}>
        <form className="space-y-4" noValidate onSubmit={(e) => e.preventDefault()}>
          {currentStep === STEP_OFFERS_MODALITIES && <OfferModalityStep form={form} />}
          {currentStep === STEP_ESSENCE && <EssenceStep form={form} />}
          {currentStep === STEP_CATALOG && (
            <CatalogStep
              form={form}
              isDigitalOnly={
                form.watch('offers').length === 1 && form.watch('offers')[0] === 'products_digital'
              }
            />
          )}
          {currentStep === STEP_OPERATION && (
            <OperationStep
              form={form}
              modalities={watchedModalities}
              offers={watchedOffers}
              language={profile?.language ?? i18n.language}
            />
          )}
          {currentStep === STEP_OBJECTIVE && <ObjectiveStep form={form} />}
          {currentStep === STEP_REVIEW && (
            <ReviewStep
              values={formValues}
              onActivate={handleActivate}
              isSubmitting={submitting}
              submitError={submitError}
            />
          )}
        </form>
      </Form>

      {/* KB size indicator — shown on steps 2–5 (not review). Live estimate
          while typing; the backend's real knowledge_size provides the ceiling
          and reconciles after save (F074). */}
      {currentStep > STEP_OFFERS_MODALITIES && currentStep < STEP_REVIEW && (
        <KbSizeIndicator
          size={kbProfile?.knowledge_size ?? null}
          estimatedChars={estimateKbChars(formValues)}
        />
      )}

      {/* Navigation footer. F149 — sticky at the viewport bottom below `sm` so
          Voltar/Próximo stay reachable on long steps (Catalog/Operation)
          instead of being buried thousands of px down; unchanged on desktop. */}
      <div
        data-testid="wizard-nav-footer"
        className="sticky bottom-0 z-10 -mb-px flex items-center justify-between gap-3 border-t border-border bg-background pt-2 pb-[env(safe-area-inset-bottom)] sm:static sm:z-auto sm:border-t-0 sm:bg-transparent sm:pb-0"
      >
        <Button
          type="button"
          variant="outline"
          className="h-11"
          onClick={handleBack}
          disabled={currentStep === 0}
          aria-label={t('onboarding.kbForm.nav.back')}
        >
          {t('onboarding.kbForm.nav.back')}
        </Button>

        {currentStep < STEP_REVIEW && (
          <Button
            type="button"
            className="h-11"
            onClick={handleNext}
            aria-label={t('onboarding.kbForm.nav.next')}
            data-testid="next-button"
          >
            {t('onboarding.kbForm.nav.next')}
          </Button>
        )}
      </div>
    </div>
  );
}
